/**
 * Hand-rolled FLIP for task rows (no motion library, per house style): when
 * the ordered key list changes, rows that moved get an inverted translateY
 * that transitions back to zero, so completing a task visibly SINKS it to
 * the done pile instead of teleporting.
 */
import { useLayoutEffect, useRef } from "react";

export function useFlip(orderKey: string): (key: string) => (el: HTMLElement | null) => void {
  const els = useRef(new Map<string, HTMLElement>());
  const prevTops = useRef(new Map<string, number>());

  useLayoutEffect(() => {
    const durations = getComputedStyle(document.documentElement).getPropertyValue("--task-dur-sink").trim();
    const dur = durations || "260ms";
    for (const [key, el] of els.current) {
      const prev = prevTops.current.get(key);
      const next = el.getBoundingClientRect().top;
      if (prev !== undefined && Math.abs(prev - next) > 1) {
        el.style.transition = "none";
        el.style.transform = `translateY(${prev - next}px)`;
        // force reflow so the transform lands before transitioning back
        void el.offsetHeight;
        el.style.transition = `transform ${dur} cubic-bezier(0.2, 0.8, 0.2, 1)`;
        el.style.transform = "";
      }
      prevTops.current.set(key, next);
    }
    // rows that left the DOM
    for (const key of [...prevTops.current.keys()]) {
      if (!els.current.has(key)) prevTops.current.delete(key);
    }
  }, [orderKey]);

  return (key: string) => (el: HTMLElement | null) => {
    if (el) {
      els.current.set(key, el);
      if (!prevTops.current.has(key)) prevTops.current.set(key, el.getBoundingClientRect().top);
    } else {
      els.current.delete(key);
    }
  };
}
